import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { supabase } from '../../../lib/supabase'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Loader2, Pause, Play, Trash2, Edit, Calendar, DollarSign, CreditCard, Plus, TrendingUp, Zap, CheckCircle, XCircle, ArrowRight, Building2, User, Mail, AlertCircle, Bell } from 'lucide-react'

export default function DonorSettings() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [recurringDonations, setRecurringDonations] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)
  const [roundupSettings, setRoundupSettings] = useState(null)
  const [roundupLoading, setRoundupLoading] = useState(true)
  const [pendingAmount, setPendingAmount] = useState(0)
  const [undonatedCount, setUndonatedCount] = useState(0)

  // Profile editing state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSuccess, setProfileSuccess] = useState(false)
  const [profileError, setProfileError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [notifPrefs, setNotifPrefs] = useState(null)
  const [notifLoading, setNotifLoading] = useState(true)

  useEffect(() => {
    if (user?.id) {
      fetchRecurringDonations()
    }
    fetchRoundupStatus()
    fetchProfile()
    api.getNotificationPreferences()
      .then(data => setNotifPrefs(data))
      .catch(err => console.error('Failed to load notification preferences', err))
      .finally(() => setNotifLoading(false))
  }, [user?.id])

  const fetchRecurringDonations = async () => {
    try {
      setLoading(true)
      const donations = await api.getUserRecurringDonations()
      setRecurringDonations(donations || [])
    } catch (err) {
      console.error('Failed to fetch recurring donations:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchRoundupStatus = async () => {
    try {
      setRoundupLoading(true)
      const [settings, txData] = await Promise.all([
        api.getRoundupSettings(),
        api.getPlaidTransactions().catch(() => ({})),
      ])
      setRoundupSettings(settings)
      const boost = settings?.roundupBoostFactor || 1
      const raw = txData?.undonatedRoundupAmount || 0
      setPendingAmount(Math.round(raw * boost * 100) / 100)
      setUndonatedCount(txData?.undonatedCount || 0)
    } catch (err) {
      console.error('Failed to fetch roundup status:', err)
    } finally {
      setRoundupLoading(false)
    }
  }

  const fetchProfile = async () => {
    try {
      setProfileLoading(true)
      const data = await api.getUser()
      setFirstName(data.firstName || '')
      setLastName(data.lastName || '')
    } catch (err) {
      console.error('Failed to fetch profile:', err)
    } finally {
      setProfileLoading(false)
    }
  }

  const handleProfileSave = async (e) => {
    e.preventDefault()
    setProfileSaving(true)
    setProfileError(null)
    setProfileSuccess(false)
    try {
      const first = firstName.trim()
      const last = lastName.trim()
      await api.updateUser(user.id, {
        firstName: first || null,
        lastName: last || null,
        name: [first, last].filter(Boolean).join(' ') || null,
      })
      setProfileSuccess(true)
      setTimeout(() => setProfileSuccess(false), 3000)
    } catch (err) {
      setProfileError(err.message || 'Failed to save changes')
    } finally {
      setProfileSaving(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleteLoading(true)
    setProfileError(null)
    try {
      await api.deleteAccount(user.id)
      await supabase.auth.signOut()
      navigate('/')
    } catch (err) {
      setProfileError(err.message || 'Failed to delete account')
      setDeleteLoading(false)
    }
  }

  const handleNotifToggle = async (key) => {
    const prev = notifPrefs;
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    try {
      await api.updateNotificationPreferences({ [key]: updated[key] });
    } catch (err) {
      setNotifPrefs(prev);
      console.error('Failed to update notification preference', err);
    }
  };

  const isGoogleUser =
    user?.app_metadata?.provider === 'google' ||
    user?.identities?.some((i) => i.provider === 'google')

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400'
      case 'paused':
        return 'text-yellow-400'
      case 'canceled':
        return 'text-red-600'
      case 'past_due':
        return 'text-orange-400'
      default:
        return 'text-charcoal-muted'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Active'
      case 'paused':
        return 'Paused'
      case 'canceled':
        return 'Canceled'
      case 'past_due':
        return 'Past Due'
      default:
        return status
    }
  }

  const handlePause = async (id) => {
    try {
      setActionLoading(id)
      await api.pauseRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      console.error('Failed to pause donation:', err)
      alert(err.message || 'Failed to pause donation')
    } finally {
      setActionLoading(null)
    }
  }

  const handleResume = async (id) => {
    try {
      setActionLoading(id)
      await api.resumeRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      console.error('Failed to resume donation:', err)
      alert(err.message || 'Failed to resume donation')
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this recurring donation? This action cannot be undone.')) {
      return
    }

    try {
      setActionLoading(id)
      await api.cancelRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      console.error('Failed to cancel donation:', err)
      alert(err.message || 'Failed to cancel donation')
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div>
      <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Settings</h1>
      
      {/* Recurring Donations Section */}
      <div className="bg-white border border-surface-border rounded p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold font-display text-charcoal mb-2">
              Recurring Donations
            </h2>
            <p className="text-charcoal-muted text-sm">
              Manage your automatic recurring donations
            </p>
          </div>
          <Button
            onClick={() => navigate('/app/donor/recurring-donation-setup')}
            variant="outline"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Set Up New
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : recurringDonations.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-charcoal-muted mb-4">
              You don't have any recurring donations set up yet.
            </p>
            <Button
              onClick={() => navigate('/app/donor/recurring-donation-setup')}
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Set Up Recurring Donation
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {recurringDonations.map((donation) => (
              <div
                key={donation.id}
                className="bg-white border border-surface-border rounded p-4 hover:border-gold/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-charcoal">
                        {donation.parish?.name || 'Unknown Parish'}
                      </h3>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(donation.status)} bg-opacity-20`}>
                        {getStatusLabel(donation.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-charcoal-muted">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-semibold text-charcoal">
                          {formatCurrency(donation.amountCents)}
                        </span>
                        <span className="text-charcoal-subtle">
                          {donation.interval === 'weekly' ? 'per week' : 'per month'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-charcoal-muted">
                        <Calendar className="w-4 h-4" />
                        <span>Started {formatDate(donation.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-charcoal-muted">
                        <CreditCard className="w-4 h-4" />
                        <span>Payment method on file</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t border-surface-border">
                  {donation.status === 'active' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePause(donation.id)}
                        disabled={actionLoading === donation.id}
                      >
                        {actionLoading === donation.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Pause className="w-4 h-4 mr-2" />
                            Pause
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/app/donor/recurring-donations/${donation.id}/edit`)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancel(donation.id)}
                        disabled={actionLoading === donation.id}
                        className="text-red-600 hover:text-red-600 hover:border-red-400"
                      >
                        {actionLoading === donation.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Cancel
                          </>
                        )}
                      </Button>
                    </>
                  )}

                  {donation.status === 'paused' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResume(donation.id)}
                        disabled={actionLoading === donation.id}
                      >
                        {actionLoading === donation.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Resume
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancel(donation.id)}
                        disabled={actionLoading === donation.id}
                        className="text-red-600 hover:text-red-600 hover:border-red-400"
                      >
                        {actionLoading === donation.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Cancel
                          </>
                        )}
                      </Button>
                    </>
                  )}

                  {donation.status === 'canceled' && (
                    <p className="text-charcoal-subtle text-sm">
                      This recurring donation has been canceled.
                    </p>
                  )}

                  {donation.status === 'past_due' && (
                    <p className="text-orange-400 text-sm">
                      Payment failed. Please update your payment method.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Round-Ups Section — only shown when enabled */}
      {!roundupLoading && roundupSettings?.roundupsEnabled && (
        <div className="bg-white border border-surface-border rounded p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-semibold font-display text-charcoal">Round-Ups</h2>
                <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  <CheckCircle className="w-3 h-3" /> Active
                </span>
              </div>
              <p className="text-charcoal-muted text-sm">Spare change from purchases donated automatically</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/app/donor/roundups')}
              className="shrink-0 border-gold/40 text-charcoal hover:border-gold"
            >
              Manage <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Parish */}
            <div className="bg-surface-linen rounded p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Building2 className="w-3.5 h-3.5 text-gold" />
                <span className="text-xs text-charcoal-muted uppercase tracking-wide font-medium">Parish</span>
              </div>
              <p className="text-sm font-semibold text-charcoal truncate">
                {roundupSettings.roundupParish?.name || '—'}
              </p>
            </div>

            {/* Boost */}
            <div className="bg-surface-linen rounded p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Zap className="w-3.5 h-3.5 text-gold" />
                <span className="text-xs text-charcoal-muted uppercase tracking-wide font-medium">Boost</span>
              </div>
              <p className="text-sm font-semibold text-charcoal">
                {roundupSettings.roundupBoostFactor && roundupSettings.roundupBoostFactor > 1
                  ? `${roundupSettings.roundupBoostFactor}×`
                  : 'Standard'}
              </p>
            </div>

            {/* Pending */}
            <div className="bg-surface-linen rounded p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-gold" />
                <span className="text-xs text-charcoal-muted uppercase tracking-wide font-medium">Pending</span>
              </div>
              <p className="text-sm font-semibold text-charcoal">
                ${pendingAmount.toFixed(2)}
              </p>
              <p className="text-xs text-charcoal-muted mt-0.5">{undonatedCount} txn{undonatedCount !== 1 ? 's' : ''}</p>
            </div>

            {/* Payment */}
            <div className="bg-surface-linen rounded p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <CreditCard className="w-3.5 h-3.5 text-gold" />
                <span className="text-xs text-charcoal-muted uppercase tracking-wide font-medium">Payment</span>
              </div>
              {roundupSettings.hasPaymentMethod ? (
                <p className="text-sm font-semibold text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> Card saved
                </p>
              ) : (
                <p className="text-sm font-semibold text-red-600 flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> Not set
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Notifications Section */}
      <div className="bg-white border border-surface-border rounded p-6 mb-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 border border-gold/40 rounded flex items-center justify-center bg-gold/10">
            <Bell className="w-4 h-4 text-gold" />
          </div>
          <div>
            <h2 className="text-xl font-semibold font-display text-charcoal">Email Notifications</h2>
            <p className="text-charcoal-muted text-sm">Choose which emails you'd like to receive</p>
          </div>
        </div>

        {notifLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : notifPrefs ? (
          <div className="space-y-4">
            {[
              { key: 'donationReceipts', label: 'Donation Notifications', description: 'Get a push notification when a donation is processed. Email receipts are always sent.' },
              { key: 'donationFailed', label: 'Payment Failure Alerts', description: 'Get notified if a payment fails' },
              { key: 'recurringLifecycle', label: 'Recurring Donation Updates', description: 'Updates when subscriptions are created, paused, resumed, or canceled' },
              { key: 'roundupLifecycle', label: 'Round-Up Updates', description: 'Get notified when round-ups are enabled or disabled' },
              { key: 'announcementNotifications', label: 'Parish Announcements', description: 'Receive emails when your parishes post announcements' },
            ].map(({ key, label, description }) => (
              <div key={key} className="flex items-center justify-between py-3 border-b border-surface-border last:border-b-0">
                <div>
                  <p className="text-sm font-medium text-charcoal">{label}</p>
                  <p className="text-xs text-charcoal-muted mt-0.5">{description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleNotifToggle(key)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    notifPrefs[key] ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      notifPrefs[key] ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-charcoal-muted text-sm text-center py-4">Unable to load notification preferences.</p>
        )}
      </div>

      {/* Account Settings Section */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 border border-gold/40 rounded flex items-center justify-center bg-gold/10">
            <User className="w-4 h-4 text-gold" />
          </div>
          <div>
            <h2 className="text-xl font-semibold font-display text-charcoal">Account Settings</h2>
            <p className="text-charcoal-muted text-sm">Manage your profile and account</p>
          </div>
        </div>

        {profileLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleProfileSave} className="space-y-4 max-w-md">
            {profileError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                <p className="text-red-600 text-sm">{profileError}</p>
              </div>
            )}
            {profileSuccess && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <p className="text-emerald-600 text-sm">Profile updated successfully</p>
              </div>
            )}

            {/* Email — read-only */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1.5">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-muted" />
                <Input
                  value={user?.email || ''}
                  readOnly
                  className="pl-9 bg-surface-linen border-surface-border text-charcoal-muted cursor-default select-none"
                />
              </div>
              <p className="text-xs text-charcoal-muted mt-1">
                {isGoogleUser
                  ? 'Your email is managed by Google and cannot be changed here.'
                  : 'Email cannot be changed. Contact support if needed.'}
              </p>
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1.5">
                First name
              </label>
              <Input
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setProfileError(null) }}
                placeholder="Enter your first name"
                maxLength={100}
                className="bg-surface-linen border-surface-border text-charcoal"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1.5">
                Last name
              </label>
              <Input
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setProfileError(null) }}
                placeholder="Enter your last name"
                maxLength={100}
                className="bg-surface-linen border-surface-border text-charcoal"
              />
            </div>

            {isGoogleUser && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-blue-700 text-xs">
                  Your account is linked to Google. You can update your name above,
                  but email and password are managed through your Google account.
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={profileSaving}
              className="bg-gold text-black font-semibold hover:bg-gold/90"
            >
              {profileSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                'Save Changes'
              )}
            </Button>
          </form>
        )}

        {/* Danger Zone */}
        {!profileLoading && (
          <div className="mt-8 pt-6 border-t border-surface-border">
            <h3 className="text-sm font-semibold text-red-600 mb-3">Danger Zone</h3>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="text-left px-4 py-3 rounded border border-red-200 bg-red-50 text-red-600 text-sm hover:bg-red-100 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4 shrink-0" />
                Delete Account
              </button>
            ) : (
              <div className="p-4 rounded border border-red-300 bg-red-50 space-y-3 max-w-md">
                <p className="text-red-700 text-sm font-medium">Are you absolutely sure?</p>
                <p className="text-red-600 text-xs leading-relaxed">
                  This will permanently delete your account and all associated data including
                  donation history. This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="flex-1 px-3 py-2 text-sm rounded border border-surface-border text-charcoal hover:bg-surface-linen transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    className="flex-1 px-3 py-2 text-sm rounded border border-red-400 bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {deleteLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" />Deleting...</>
                    ) : (
                      <><Trash2 className="w-3.5 h-3.5" />Yes, Delete</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
